/**
 * Statistics (Foundry: src/module/system/statistic): a check modifier and a DC derived from an
 * attribute modifier, a proficiency modifier, and every synthetic modifier whose selector
 * matches one of the statistic's domains, after predicate tests, adjustments and stacking. The
 * check also gathers what a Roll of it consumes (#19): notes, degree-of-success adjustments,
 * roll-twice, substitutions and ephemeral effects.
 */
import { applyStackingRules, cloneModifier, createAttributeModifier, createProficiencyModifier, dedupeModifiers, resolveModifiers, signed, testModifier, type Modifier } from "./modifiers";
import { testBeforeRoll, testPredicate } from "./predicate";
import type { EngineState } from "./state";
import { extractDegreeOfSuccessAdjustments, extractEphemeralEffects, extractModifierAdjustments, extractModifiersForDomains, extractNotes, extractRollTwice, type DegreeOfSuccessAdjustmentSynthetic, type EphemeralEffectSynthetic, type RollNote, type RollSubstitution, type RollTwiceSynthetic } from "./synthetics";
import { PROFICIENCY_RANK_OPTION, type Attribute, type Rank } from "./values";

export type CheckType = "check" | "attack-roll" | "saving-throw" | "skill-check" | "perception-check" | "initiative" | "flat-check";

export interface StatisticSpec {
  slug: string;
  label: string;
  attribute?: Attribute | null;
  rank?: Rank | null;
  domains: string[];
  modifiers?: Modifier[];
  check?: { type: CheckType; domains?: string[]; modifiers?: Modifier[] };
  dc?: { domains?: string[]; modifiers?: Modifier[]; label?: string };
  rollOptions?: string[];
  lore?: boolean;
  /** Cap on the attribute modifier (armour's Dexterity cap). */
  attributeMax?: number;
}

/** A note with the check's own predicate test applied. */
export interface CheckNote extends RollNote {
  enabled: boolean;
  /** The predicate names the rolled total (`check:total:natural:19`): enabled subject to that, tested again after the roll. */
  postRoll: boolean;
}

export interface CheckDegreeAdjustment extends DegreeOfSuccessAdjustmentSynthetic {
  enabled: boolean;
  postRoll: boolean;
}

export interface CheckSubstitution extends Omit<RollSubstitution, "removeAfterRoll"> {
  enabled: boolean;
  /** Resolved for this statistic's options: ends the Effect after any Roll, or only one that used the substitution. */
  removeAfterRoll: boolean | "if-enabled";
}

/** A RollTwice Rule Element on the check, with whether its predicate passes for the statistic's own options. */
export interface CheckRollTwice extends RollTwiceSynthetic {
  enabled: boolean;
}

export interface CheckEphemeralEffect extends EphemeralEffectSynthetic {
  enabled: boolean;
}

export interface DerivedCheck {
  domains: string[];
  options: Set<string>;
  modifiers: Modifier[];
  mod: number;
  breakdown: string;
  notes: CheckNote[];
  degreeAdjustments: CheckDegreeAdjustment[];
  rollTwice: "keep-higher" | "keep-lower" | false;
  /** Every RollTwice on the check's domains, so a Roll (#19) can name its source and end it after the roll. */
  rollTwiceSources: CheckRollTwice[];
  substitutions: CheckSubstitution[];
  ephemeralEffects: CheckEphemeralEffect[];
}

export interface DerivedDC {
  domains: string[];
  modifiers: Modifier[];
  value: number;
  breakdown: string;
}

export interface DerivedStatistic {
  slug: string;
  label: string;
  attribute: Attribute | null;
  rank: Rank | null;
  lore: boolean;
  domains: string[];
  /** The modifiers common to check and DC, after the base roll options test. */
  modifiers: Modifier[];
  check: DerivedCheck;
  dc: DerivedDC;
  /** What it was built from, so a SpecialStatistic can extend it. */
  spec: StatisticSpec;
}

function unique<T>(values: T[]): T[] {
  return [...new Set(values)];
}

export function createRollOptions(state: EngineState, domains: string[], rank: Rank | null, extra: string[] = []): Set<string> {
  const options = state.rollOptions.get(domains);
  if (typeof rank === "number") options.add(PROFICIENCY_RANK_OPTION[rank]);
  for (const o of extra) options.add(o);
  return options;
}

export function breakdownOf(modifiers: Modifier[], base?: string): string {
  return [base, ...modifiers.filter((m) => m.enabled).map((m) => `${m.label} ${signed(m.value)}`)].filter((s): s is string => !!s).join(", ");
}

/** What a Roll of a check consumes: notes, degree adjustments, roll-twice, substitutions, ephemeral effects. */
export type CheckExtras = Pick<DerivedCheck, "notes" | "degreeAdjustments" | "rollTwice" | "rollTwiceSources" | "substitutions" | "ephemeralEffects">;

export function collectCheckExtras(state: EngineState, domains: string[], options: Set<string>): CheckExtras {
  const synthetics = state.synthetics;
  const test = (predicate: RollNote["predicate"], parentOptions: string[]): boolean => predicate.length === 0 || testPredicate(predicate, new Set([...options, ...parentOptions]));
  const before = (predicate: RollNote["predicate"], parentOptions: string[]): { enabled: boolean; postRoll: boolean } => {
    if (predicate.length === 0) return { enabled: true, postRoll: false };
    const r = testBeforeRoll(predicate, new Set([...options, ...parentOptions]));
    return { enabled: r.passes, postRoll: r.postRoll };
  };
  const notes = extractNotes(synthetics.rollNotes, domains).map((n) => ({ ...n, ...before(n.predicate, n.parentOptions) }));
  const degreeAdjustments = extractDegreeOfSuccessAdjustments(synthetics, domains).map((a) => ({ ...a, ...before(a.predicate, a.parentOptions) }));
  const rollTwice = extractRollTwice(synthetics.rollTwice, domains, options);
  const rollTwiceSources = unique(domains).flatMap((d) => (synthetics.rollTwice[d] ?? []).map((rt) => ({ ...rt, predicate: structuredClone(rt.predicate), enabled: test(rt.predicate, rt.parentOptions) })));
  const substitutions = unique(domains).flatMap((d) =>
    (synthetics.rollSubstitutions[d] ?? []).map((s) => ({
      ...s,
      enabled: test(s.predicate, s.parentOptions),
      removeAfterRoll: s.removeAfterRoll === true || s.removeAfterRoll === "if-enabled" ? s.removeAfterRoll : Array.isArray(s.removeAfterRoll) && s.removeAfterRoll.length > 0 ? test(s.removeAfterRoll, s.parentOptions) : false,
    })),
  );
  const ephemeralEffects = extractEphemeralEffects(synthetics, domains).map((e) => ({ ...e, enabled: e.test(options) }));
  return { notes, degreeAdjustments, rollTwice, rollTwiceSources, substitutions, ephemeralEffects };
}

/** Derive one statistic. Mirrors Statistic -> StatisticCheck / StatisticDifficultyClass. */
export function buildStatistic(state: EngineState, spec: StatisticSpec): DerivedStatistic {
  const domains = unique([...spec.domains]);
  const attribute = spec.attribute ?? null;
  const rank = spec.rank ?? null;
  const level = state.level;
  const adjustmentsFor = (slug: string, forDomains: string[]) => extractModifierAdjustments(state.synthetics.modifierAdjustments, forDomains, slug);

  if (attribute && !domains.includes(`${attribute}-based`)) domains.push(`${attribute}-based`);
  const attributeModifier = attribute
    ? (spec.modifiers?.find((m) => m.type === "ability" && m.ability === attribute) ?? createAttributeModifier(attribute, state.actor.system.abilities[attribute].mod, domains, spec.attributeMax, adjustmentsFor(attribute, domains)))
    : null;
  const proficiencyModifier =
    typeof rank === "number"
      ? (spec.modifiers?.find((m) => m.type === "proficiency") ?? createProficiencyModifier({ rank, level, withoutLevel: state.variantRules.proficiencyWithoutLevel, adjustments: adjustmentsFor("proficiency", domains) }))
      : null;
  const baseModifiers = [attributeModifier, proficiencyModifier].filter((m): m is Modifier => !!m);
  const activeSlugs = new Set(baseModifiers.map((m) => m.slug));
  const ownModifiers = [...baseModifiers, ...(spec.modifiers ?? []).filter((m) => !activeSlugs.has(m.slug))];
  for (const m of ownModifiers) if (m.adjustments.length === 0) m.adjustments = adjustmentsFor(m.slug, domains);

  // BaseStatistic: extract, clone, test against the base domains, de-duplicate.
  const baseOptions = domains.length > 0 ? createRollOptions(state, domains, rank, spec.rollOptions) : null;
  const extracted = extractModifiersForDomains(state, domains);
  const tested = [...ownModifiers, ...extracted].map((m) => {
    const clone = cloneModifier(m, { domains: [...domains] });
    if (baseOptions) testModifier(clone, baseOptions);
    return clone;
  });
  const modifiers = dedupeModifiers(tested);

  // StatisticCheck.
  const checkType = spec.check?.type ?? "check";
  const checkDomains = new Set(["check", ...(spec.check?.domains ?? [])]);
  if (checkType === "attack-roll") {
    checkDomains.add("attack");
    checkDomains.add("attack-roll");
    checkDomains.add(`${spec.slug}-attack-roll`);
  } else {
    checkDomains.add(`${spec.slug}-check`);
    if (checkType === "flat-check") {
      checkDomains.delete("check");
      checkDomains.add("flat-check");
    }
  }
  const checkOnlyDomains = [...checkDomains];
  const allCheckDomains = unique([...domains, ...checkOnlyDomains]);
  const checkOptions = createRollOptions(state, allCheckDomains, rank, spec.rollOptions);
  const checkModifiers = [
    ...modifiers.map((m) => cloneModifier(m)),
    ...[...(spec.check?.modifiers ?? []), ...extractModifiersForDomains(state, checkOnlyDomains)].map((m) => {
      const clone = cloneModifier(m, { domains: [...allCheckDomains] });
      if (clone.adjustments.length === 0) clone.adjustments = adjustmentsFor(clone.slug, allCheckDomains);
      testModifier(clone, checkOptions);
      return clone;
    }),
  ];
  const check = resolveModifiers(checkModifiers, checkOptions);
  const extras = collectCheckExtras(state, allCheckDomains, checkOptions);

  // StatisticDifficultyClass.
  const dcOnlyDomains = spec.dc?.domains ?? [`${spec.slug}-dc`];
  const dcDomains = unique([...domains, ...dcOnlyDomains]);
  const dcOptions = createRollOptions(state, dcDomains, rank, spec.rollOptions);
  const dcModifiers = [...modifiers.map((m) => cloneModifier(m)), ...[...(spec.dc?.modifiers ?? []), ...extractModifiersForDomains(state, dcOnlyDomains)].map((m) => cloneModifier(m))];
  const dc = resolveModifiers(dcModifiers, dcOptions);

  return {
    slug: spec.slug,
    label: spec.label,
    attribute,
    rank,
    lore: spec.lore ?? false,
    domains,
    modifiers,
    check: { domains: allCheckDomains, options: checkOptions, modifiers: check.modifiers, mod: check.total, breakdown: breakdownOf(check.modifiers), ...extras },
    dc: { domains: dcDomains, modifiers: dc.modifiers, value: 10 + dc.total, breakdown: breakdownOf(dc.modifiers, "10") },
    spec,
  };
}

/** Re-stack a check's modifiers after something changed them in place (a battle form), refreshing the total and breakdown. */
export function recomputeCheck(check: DerivedCheck): void {
  check.mod = applyStackingRules(check.modifiers);
  check.breakdown = breakdownOf(check.modifiers);
}

/** Re-stack a DC's modifiers after something changed them in place. */
export function recomputeDC(dc: DerivedDC): void {
  dc.value = 10 + applyStackingRules(dc.modifiers);
  dc.breakdown = breakdownOf(dc.modifiers, "10");
}
